/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CentreBase } from '@cambridgeassessment/lib-ee-commons';
import { CentralModalStyled, FixedScrollableHeight } from '../ManageSampleChanges.styles';
import thunks from '../../../../store/samplesManagement/thunks';
import Loader from '../../../../components/Loader/Loader';
import { RootState } from '../../../../store';
import { filterLearners } from '../helpers';
import { setIsEntriesPatching } from '../../../../store/samplesManagement/actions';
import { Entry } from '@cambridgeassessment/lib-ee-commons/src/domain/db/entries';
import { LearnersReplacementTable } from './LearnersReplacementTable';
import { ModalTopSection } from '../ModalTopSection';

interface Props {
  onCloseClick: () => void;
  sampleId: string;
  refetchList: () => void;
  learnerOut: Entry | undefined;
}

export const LearnerReplacementModal = (props: Props): JSX.Element => {
  const { onCloseClick, sampleId, refetchList, learnerOut } = props;
  const dispatch = useDispatch();

  const {
    samplesManagement: { centreLearners, isEntriesPatching, isEntriesLoading, sampleSearchParams, consortium, sample },
    userData: { businessUnit },
  } = useSelector((state: RootState) => state);

  const [searchValue, setSearchValue] = useState('');
  const [filteredLearners, setFilteredLearners] = useState<Entry[]>();

  const onSearchInputChange = (value: string): void => setSearchValue(value);

  const [learnerIn, setLearnerIn] = useState<Entry>();

  const [centreSelectVal, setCentreSelectVal] = useState<CentreBase | null>(
    consortium?.centres.filter((x) => x.id === sampleSearchParams?.centreId?.toUpperCase())[0] ?? null,
  );

  const centreOrConsortiumLearners = centreSelectVal
    ? centreLearners
        .filter((learner) => learner.centreId === centreSelectVal?.id)
        .sort((a, b) => Number(b.mark) - Number(a.mark))
    : centreLearners.sort((a, b) => Number(b.mark) - Number(a.mark));

  useEffect(() => {
    setFilteredLearners(filterLearners(searchValue, centreLearners, centreSelectVal?.id));
  }, [searchValue]);

  useEffect(() => {
    if (businessUnit) {
      dispatch(thunks.getCentreLearners(businessUnit.id, sample?._id ?? ''));
    }
  }, [businessUnit, dispatch, centreSelectVal]);

  useEffect(() => {
    if (isEntriesPatching === false) {
      refetchList();
      dispatch(setIsEntriesPatching());
      onCloseClick();
    }
  }, [isEntriesPatching]);

  const headerStr = `Replace candidate ${learnerOut?.learner.number} - ${learnerOut?.learner.name} - ${learnerOut?.mark}/${sample?.maxMark}`;

  return (
    <CentralModalStyled
      open
      dataQaId='learner-replacement-modal'
      btnRightText='Replace candidate'
      btnLeftText='Cancel'
      onClickBtnRight={() => {
        if (learnerIn && learnerOut && businessUnit?.id) {
          dispatch(thunks.replaceLearner(businessUnit?.id, learnerOut, learnerIn, sampleId));
        }
      }}
      onClickBtnLeft={onCloseClick}
      btnRightDisabled={!learnerIn}
    >
      <div>
        <ModalTopSection
          centreSelectVal={centreSelectVal}
          consortium={consortium}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          headerStr={headerStr}
          setCentreSelectVal={setCentreSelectVal}
          onSearchInputChange={onSearchInputChange}
          dataQaId='replace'
        />
        <FixedScrollableHeight>
          {isEntriesLoading || isEntriesPatching ? (
            <Loader />
          ) : (
            <LearnersReplacementTable
              setLearnerIn={setLearnerIn}
              learnerIn={learnerIn}
              filteredLearnersList={filteredLearners ?? centreOrConsortiumLearners}
              isPartOfConsortia={!!consortium}
              maxMark={sample?.maxMark}
            />
          )}
        </FixedScrollableHeight>
      </div>
    </CentralModalStyled>
  );
};
